import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PortalShell from "@/components/portal/PortalShell";
import { DocUploader } from "@/components/portal/DocUploader";
import { DocItemRow } from "@/components/portal/DocItemRow";
import { DocsProgress } from "@/components/portal/crm/DocsProgress";
import { Loader2, CheckCircle2, ShieldCheck, Smartphone } from "lucide-react";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

const helpCta = (
  <a href="mailto:ops@connex.is" className="rounded-full border border-white/15 hover:border-white/30 px-3.5 h-9 inline-flex items-center text-white transition-colors" data-testid="docs-help">
    Need a hand?
  </a>
);

/** ClientDocUpload — the applicant sends in what the lender asked for. No login, phone-first. */
export default function ClientDocUpload() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/apply/docs/${token}`);
        if (alive) { setData(r.data); setDone((r.data.files || []).map((f, i) => ({ id: `pre-${i}`, filename: f.filename, size: 0 }))); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "That upload link isn't valid — ask whoever sent it for a new one");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return <div className="min-h-screen bg-[#0B1220] grid place-items-center"><Loader2 className="size-6 animate-spin text-[#7da9ff]" /></div>;
  }

  if (err) {
    return (
      <PortalShell variant="apply" kind="introducer" ctas={helpCta}
                   eyebrow="Connexis · Secure document upload" title="We couldn't open that link">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 max-w-xl" data-testid="docs-error">
          <p className="text-[14px] text-slate-300 leading-relaxed">{err}</p>
          <p className="mt-3 text-[13px] text-slate-400">
            Links are private to one application and expire after 30 days. Reply to the message you received
            and we'll send a fresh one straight away.
          </p>
        </div>
      </PortalShell>
    );
  }

  const uploaded = done.length;
  const remaining = Math.max(0, (data.max_files || 12) - uploaded);
  const progress = data.progress;
  const allIn = progress?.total > 0 && progress.in >= progress.total;
  const items = data.item_status || (data.items || []).map((label) => ({ label, done: false, files: [] }));

  const itemDone = (idx, res) => {
    setDone((d) => [...d, { id: res.id, filename: res.filename, size: res.size }]);
    setData((d) => ({
      ...d, progress: res.progress || d.progress,
      item_status: items.map((it, i) => (i === idx ? { ...it, done: true, files: [...(it.files || []), res.filename] } : it)),
    }));
  };
  const generalDone = (list) => {
    setDone(list);
    const last = [...list].reverse().find((f) => f.progress);
    if (!last) return;
    setData((d) => ({
      ...d, progress: last.progress,
      item_status: items.map((it, i) => (i === last.item ? { ...it, done: true, files: [...(it.files || []), last.filename] } : it)),
    }));
  };

  return (
    <PortalShell variant="apply" kind="introducer" ctas={helpCta}
                 eyebrow="Connexis · Secure document upload"
                 title={allIn ? "Thank you — that's everything" : uploaded ? "Thank you — that's with us" : "A couple of documents and you're done"}
                 subtitle={data.company_name
                   ? `For ${data.company_name}${data.reference ? ` · ${data.reference}` : ""}`
                   : "Your finance application"}>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
        {/* Upload */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="docs-upload-card">
          <div className="mb-5">
            <DocsProgress progress={progress} testId="docs-progress" />
          </div>

          {uploaded > 0 && (
            <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5" data-testid="docs-received">
              <CheckCircle2 className="size-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-[12.5px] text-emerald-900">
                <b>{uploaded} document{uploaded === 1 ? "" : "s"} received.</b> The team handling your application has been
                notified{allIn ? " — there's nothing else you need to do." : " — send the rest below whenever you have them."}
              </div>
            </div>
          )}

          <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-600">What's needed</div>
          <ul className="mt-2.5 space-y-2" data-testid="docs-items">
            {items.map((it, i) => <DocItemRow key={`${it.label}-${i}`} token={token} idx={i} item={it} onDone={itemDone} />)}
          </ul>
          {data.note && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12.5px] text-slate-700">
              {data.note}
            </div>
          )}

          <div className="mt-5">
            {items.length > 1 && <div className="mb-2 text-[12.5px] text-slate-500">Anything else to send? Drop it here.</div>}
            {remaining > 0 ? (
              <DocUploader files={done} onChange={generalDone} max={remaining + uploaded} allowRemove={false}
                           endpoint={`/apply/docs/${token}/upload`} testId="docs-uploader" />
            ) : (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[13px] text-slate-600">
                That's the maximum number of files for this request. If you have more to send, reply to the
                message you received and we'll sort it.
              </div>
            )}
          </div>
        </div>

        {/* Reassurance */}
        <div className="space-y-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-white">
              <Smartphone className="size-4 text-[#7da9ff]" /> A photo is fine
            </div>
            <p className="mt-1.5 text-[12.5px] text-slate-400 leading-relaxed">
              Take a picture of each document with your phone camera and upload it here. PDFs, scans and
              screenshots all work.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-white">
              <ShieldCheck className="size-4 text-[#7da9ff]" /> Private to your application
            </div>
            <p className="mt-1.5 text-[12.5px] text-slate-400 leading-relaxed">
              This link belongs to your application only and is shared with the lender considering it — nobody else.
              {data.expires_at ? ` It works until ${fmt(data.expires_at)}.` : ""}
            </p>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
